import { useEffect, useRef, useState } from "react";

// Moved verbatim from ChatLayout (step 3.1). The snapshot lets presence dots
// render instantly on reload while the socket reconnects.
function loadPresenceSnapshot() {
  try {
    const raw = localStorage.getItem('__presence_snapshot__');
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      roomUserIds: Array.isArray(parsed.roomUserIds) ? parsed.roomUserIds : [],
      spaceUserIds: Array.isArray(parsed.spaceUserIds) ? parsed.spaceUserIds : [],
      globalUserIds: Array.isArray(parsed.globalUserIds) ? parsed.globalUserIds : [],
    };
  } catch {
    return { roomUserIds: [], spaceUserIds: [], globalUserIds: [] };
  }
}

type UsePresenceOptions = {
  socket: any;
  currentVoidId: string;
  currentChannelId: string;
};

/**
 * Who's online/idle/on-mobile at room, space, and global scope, plus who is
 * typing in the current conversation. Owns the localStorage presence snapshot
 * (load + persist) and the typing/presence socket handlers.
 *
 * The socket effect re-registers on conversation change because onTypingStart/
 * onTypingStop/onPresenceSpace filter events through closures over
 * currentVoidId/currentChannelId — same cadence as the original shared socket
 * effect they were lifted from (step 3.1).
 *
 * setTypers/setRoomUserIds are returned because ChatLayout's switchVoid/
 * switchChannel reset them on navigation.
 */
export function usePresence({ socket, currentVoidId, currentChannelId }: UsePresenceOptions) {
  const [typers, setTypers] = useState<Record<string, string>>({});
  const typingTimers = useRef(new Map<string, number>());
  // Lazy initializers so the snapshot is read from localStorage once at mount,
  // not re-parsed on every render (step 3.1 cleanup).
  const [roomUserIds, setRoomUserIds] = useState<string[]>(() => loadPresenceSnapshot().roomUserIds);
  const [spaceUserIds, setSpaceUserIds] = useState<string[]>(() => loadPresenceSnapshot().spaceUserIds);
  const [globalUserIds, setGlobalUserIds] = useState<string[]>(() => loadPresenceSnapshot().globalUserIds);
  const [roomMobileIds, setRoomMobileIds] = useState<string[]>([]);
  const [spaceMobileIds, setSpaceMobileIds] = useState<string[]>([]);
  const [globalMobileIds, setGlobalMobileIds] = useState<string[]>([]);
  const [roomIdleIds, setRoomIdleIds] = useState<string[]>([]);
  const [spaceIdleIds, setSpaceIdleIds] = useState<string[]>([]);
  const [globalIdleIds, setGlobalIdleIds] = useState<string[]>([]);
  useEffect(() => {
    try {
      localStorage.setItem('__presence_snapshot__', JSON.stringify({
        roomUserIds,
        spaceUserIds,
        globalUserIds,
      }));
    } catch { /* noop */ }
  }, [roomUserIds, spaceUserIds, globalUserIds]);

  useEffect(() => {
    if (!socket) return;

    const onTypingStart = ({ voidId: v, channelId: ch, userId, name }: { voidId: string; channelId: string; userId: string, name?: string }) => {
      if (v !== currentVoidId || ch !== currentChannelId) return;
      setTypers((t) => (t[userId] ? t : { ...t, [userId]: name || userId }));
      const prev = typingTimers.current.get(userId);
      if (prev) clearTimeout(prev);
      // Auto-expire a typing indicator if no further events arrive
      const tid = window.setTimeout(() => {
        setTypers((t) => { const n = { ...t }; delete n[userId]; return n; });
        typingTimers.current.delete(userId);
      }, 8000);
      typingTimers.current.set(userId, tid);
    };
    const onTypingStop = ({ voidId: v, channelId: ch, userId }: { voidId: string; channelId: string; userId: string }) => {
      if (v !== currentVoidId || ch !== currentChannelId) return;
      const prev = typingTimers.current.get(userId);
      if (prev) clearTimeout(prev);
      // Gracefully hide after a short delay to prevent flicker from rapid start/stop
      const tid = window.setTimeout(() => {
        setTypers((t) => { const n = { ...t }; delete n[userId]; return n; });
        typingTimers.current.delete(userId);
      }, 1200);
      typingTimers.current.set(userId, tid);
    };

    const onPresenceRoom = ({ room: _room, userIds, mobileUserIds, idleUserIds }: { room: string; userIds: string[]; mobileUserIds?: string[]; idleUserIds?: string[] }) => {
      setRoomUserIds(userIds);
      setRoomMobileIds(mobileUserIds || []);
      setRoomIdleIds(idleUserIds || []);
    };
    const onPresenceSpace = ({ spaceId, userIds, mobileUserIds, idleUserIds }: { spaceId: string; userIds: string[]; mobileUserIds?: string[]; idleUserIds?: string[] }) => {
      if (spaceId !== currentVoidId) return;
      setSpaceUserIds(userIds);
      setSpaceMobileIds(mobileUserIds || []);
      setSpaceIdleIds(idleUserIds || []);
    };
    const onPresenceGlobal = ({ userIds, mobileUserIds, idleUserIds }: { userIds: string[]; mobileUserIds?: string[]; idleUserIds?: string[] }) => {
      setGlobalUserIds(userIds);
      setGlobalMobileIds(mobileUserIds || []);
      setGlobalIdleIds(idleUserIds || []);
    };

    socket.on("typing:start", onTypingStart);
    socket.on("typing:stop", onTypingStop);
    socket.on("presence:room", onPresenceRoom);
    socket.on("presence:space", onPresenceSpace);
    socket.on("presence:global", onPresenceGlobal);
    return () => {
      socket.off("typing:start", onTypingStart);
      socket.off("typing:stop", onTypingStop);
      socket.off("presence:room", onPresenceRoom);
      socket.off("presence:space", onPresenceSpace);
      socket.off("presence:global", onPresenceGlobal);
    };
  }, [socket, currentVoidId, currentChannelId]);

  return {
    typers,
    setTypers,
    roomUserIds,
    setRoomUserIds,
    spaceUserIds,
    globalUserIds,
    roomMobileIds,
    spaceMobileIds,
    globalMobileIds,
    roomIdleIds,
    spaceIdleIds,
    globalIdleIds,
  };
}


